import { useState } from 'react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../../components/ui/collapsible'
import { Badge } from '../../../../components/ui/badge'
import type { MetricData } from '../../types'
import { PILLAR_META, PILLAR_ORDER } from '../../types'

interface MetricDetailsProps {
  metrics: MetricData[]
}

const QUALITY_STYLES: Record<string, string> = {
  actual: 'bg-green-100 text-green-700',
  approximate: 'bg-orange-100 text-orange-700',
}

const CHANNEL_META: Record<string, { label: string; weight: number }> = {
  website: { label: 'Website & Search', weight: 25 },
  social: { label: 'Social Media', weight: 25 },
  paid: { label: 'Paid Advertising', weight: 20 },
  pr: { label: 'Digital PR', weight: 15 },
  gmb: { label: 'GMB', weight: 15 },
}

function extractChannel(metricKey: string): string | null {
  const match = metricKey.match(/^cs_([^_]+)_/)
  return match ? match[1] : null
}

function groupByChannel(items: MetricData[]) {
  const channels: Record<string, MetricData[]> = {}
  for (const m of items) {
    const ch = extractChannel(m.metric_key)
    if (ch) {
      if (!channels[ch]) channels[ch] = []
      channels[ch].push(m)
    }
  }
  return channels
}

export default function MetricDetails({ metrics }: MetricDetailsProps) {
  const [openPillars, setOpenPillars] = useState<Set<string>>(new Set(['CP']))
  const [openChannels, setOpenChannels] = useState<Set<string>>(new Set())

  const togglePillar = (key: string) => {
    setOpenPillars((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const toggleChannel = (key: string) => {
    setOpenChannels((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const grouped = PILLAR_ORDER.map((key) => ({
    key,
    meta: PILLAR_META[key],
    items: metrics.filter((m) => m.pillar === key && m.data_quality !== 'unavailable'),
  })).filter((g) => g.items.length > 0)

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-gray-900">Metric Details</h3>
      <div className="space-y-2">
        {grouped.map(({ key, meta, items }) => {
          const isOpen = openPillars.has(key)
          const avgScore =
            items.reduce((sum, m) => sum + (m.normalized_score || 0), 0) / items.length

          if (key === 'CS') {
            return (
              <CSPillarSection
                key={key}
                meta={meta}
                items={items}
                isOpen={isOpen}
                onToggle={() => togglePillar(key)}
                openChannels={openChannels}
                onToggleChannel={toggleChannel}
                avgScore={avgScore}
              />
            )
          }

          return (
            <Collapsible key={key} open={isOpen} onOpenChange={() => togglePillar(key)}>
              <CollapsibleTrigger className="w-full">
                <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-2">
                    <i className={`${meta.icon} text-base`} style={{ color: meta.color }} />
                    <span className="text-sm font-semibold text-gray-900">
                      {key} — {meta.label}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({key === 'CP' ? '20' : key === 'CE' ? '25' : key === 'OM' ? '20' : '15'}%)
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold" style={{ color: meta.color }}>
                      {avgScore.toFixed(1)}
                    </span>
                    <i className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line text-gray-400`} />
                  </div>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <MetricTable items={items} />
              </CollapsibleContent>
            </Collapsible>
          )
        })}
      </div>
    </div>
  )
}

function CSPillarSection({
  meta,
  items,
  isOpen,
  onToggle,
  openChannels,
  onToggleChannel,
  avgScore,
}: {
  meta: typeof PILLAR_META.CP
  items: MetricData[]
  isOpen: boolean
  onToggle: () => void
  openChannels: Set<string>
  onToggleChannel: (key: string) => void
  avgScore: number
}) {
  const channels = groupByChannel(items)

  return (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <CollapsibleTrigger className="w-full">
        <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-2">
            <i className={`${meta.icon} text-base`} style={{ color: meta.color }} />
            <span className="text-sm font-semibold text-gray-900">
              {meta.label}
            </span>
            <span className="text-xs text-gray-500">(20%)</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-bold" style={{ color: meta.color }}>
              {avgScore.toFixed(1)}
            </span>
            <i className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line text-gray-400`} />
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-1 pl-4">
          {Object.entries(CHANNEL_META).map(([chKey, chMeta]) => {
            const chItems = (channels[chKey] || []).filter((m) => m.data_quality !== 'unavailable')
            const available = chItems.length
            const total = channels[chKey]?.length || 6
            const isChOpen = openChannels.has(chKey)

            if (available === 0) {
              return null
            }

            return (
              <Collapsible key={chKey} open={isChOpen} onOpenChange={() => onToggleChannel(chKey)}>
                <CollapsibleTrigger className="w-full">
                  <div className="flex items-center justify-between p-2 border rounded hover:bg-gray-50 transition-colors text-sm">
                    <div className="flex items-center gap-2">
                      <i className={`ri-arrow-${isChOpen ? 'up' : 'down'}-s-line text-xs text-gray-400`} />
                      <span className="font-medium text-gray-700">{chMeta.label}</span>
                      <span className="text-xs text-gray-400">({chMeta.weight}%)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">{available}/{total}</span>
                      <Badge className="text-xs bg-green-100 text-green-700" variant="outline">
                        {(chItems.reduce((s, m) => s + (m.normalized_score || 0), 0) / chItems.length).toFixed(1)}
                      </Badge>
                    </div>
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <MetricTable items={chItems} nested />
                </CollapsibleContent>
              </Collapsible>
            )
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

function MetricTable({ items, nested = false }: { items: MetricData[]; nested?: boolean }) {
  const filtered = items.filter((m) => m.data_quality !== 'unavailable')
  if (filtered.length === 0) return null

  return (
    <div className={`border rounded-lg overflow-hidden ${nested ? 'ml-4 mt-1' : 'border-t-0'}`}>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-2 font-medium text-gray-500">Metric</th>
            <th className="text-right px-4 py-2 font-medium text-gray-500">Raw</th>
            <th className="text-right px-4 py-2 font-medium text-gray-500">Score</th>
            <th className="text-center px-4 py-2 font-medium text-gray-500">Quality</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {filtered.map((m) => (
            <tr key={m.metric_key} className="hover:bg-gray-50">
              <td className="px-4 py-2 text-gray-700">
                {m.metric_key.replace(/_/g, ' ')}
              </td>
              <td className="px-4 py-2 text-right text-gray-600">
                {m.raw_value !== null && m.raw_value !== undefined
                  ? typeof m.raw_value === 'number'
                    ? m.raw_value.toLocaleString()
                    : m.raw_value
                  : '—'}
              </td>
              <td className="px-4 py-2 text-right font-semibold text-gray-900">
                {typeof m.normalized_score === 'number'
                  ? m.normalized_score.toFixed(1)
                  : '—'}
              </td>
              <td className="px-4 py-2 text-center">
                <Badge
                  className={`text-xs ${QUALITY_STYLES[m.data_quality] || ''}`}
                  variant="outline"
                >
                  {m.data_quality}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}